import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { Button, Card, Input, Tag, Modal, Alert, Progress } from "antd";
import {
  LinkOutlined,
  DisconnectOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Breadcrumb from "../../components/Breadcrumb";
import { useToast } from "../../components/Toast/toastHook";
import Spinner from "../../components/Spinner";
import {
  GET_FUB_SETTINGS,
  TEST_FUB_CONNECTION,
  CONNECT_FUB,
  DISCONNECT_FUB,
  TRIGGER_FUB_SYNC,
} from "../../graphql/fubSettingQueries";

const { confirm } = Modal;

// Default FUB stage configuration (read-only)
const DEFAULT_ACTIVE_STAGES = ["Lead", "Hot Prospect", "Nurture", "Active Client", "Pending"];
const DEFAULT_CLOSED_STAGES = ["Closed", "Past Client"];
const DEFAULT_IGNORE_STAGES = ["Sphere"];

const FUBSettings = ({ embedded = false }) => {
  const { companyId } = useParams();
  const showToast = useToast();

  const [apiKey, setApiKey] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Queries
  const { data, loading, refetch } = useQuery(GET_FUB_SETTINGS, {
    variables: { companyId },
    fetchPolicy: "network-only",
  });

  const [testConnection, { loading: testingConnection }] = useLazyQuery(
    TEST_FUB_CONNECTION,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        setTestResult(data.testFubConnection);
      },
      onError: (error) => {
        setTestResult({ success: false, message: error.message });
      },
    }
  );


  // Mutations
  const [connectFub, { loading: connecting }] = useMutation(CONNECT_FUB, {
    onCompleted: (data) => {
      if (data.connectFub.success) {
        showToast("success", data.connectFub.message);
        refetch();
        setApiKey("");
        setTestResult(null);
      } else {
        showToast("error", data.connectFub.message);
      }
      setIsConnecting(false);
    },
    onError: (error) => {
      showToast("error", error.message);
      setIsConnecting(false);
    },
  });

  const [disconnectFub, { loading: disconnecting }] = useMutation(DISCONNECT_FUB, {
    onCompleted: (data) => {
      if (data.disconnectFub.success) {
        showToast("success", `${data.disconnectFub.message} (${data.disconnectFub.buyersRemoved} buyers removed)`);
        refetch();
      } else {
        showToast("error", data.disconnectFub.message);
      }
    },
    onError: (error) => {
      showToast("error", error.message);
    },
  });

  const [triggerFubSync, { loading: triggering }] = useMutation(TRIGGER_FUB_SYNC, {
    onCompleted: () => {
      showToast("success", "Sync started");
      refetch();
    },
    onError: (error) => {
      showToast("error", error.message);
    },
  });

  const settings = data?.getFubSettings;

  // Track previous syncing state to detect when sync completes
  const prevIsSyncing = useRef(null);

  // Auto-refresh and show toast when sync completes
  useEffect(() => {
    // If was syncing and now stopped, sync completed
    if (prevIsSyncing.current === true && settings?.isSyncing === false) {
      showToast("success", "Sync completed!");
      refetch(); // Final refresh to get latest data
    }
    prevIsSyncing.current = settings?.isSyncing;
  }, [settings?.isSyncing, refetch, showToast]);

  // Poll settings while syncing to show real-time progress
  useEffect(() => {
    if (settings?.isConnected && settings?.isSyncing) {
      const interval = setInterval(() => {
        refetch();
      }, 2000); // Poll every 2 seconds while syncing
      return () => clearInterval(interval);
    }
  }, [settings?.isConnected, settings?.isSyncing, refetch]);

  const handleTestConnection = () => {
    if (!apiKey.trim()) {
      showToast("error", "Please enter an API key");
      return;
    }
    testConnection({ variables: { apiKey } });
  };

  const handleConnect = () => {
    if (!testResult?.success) {
      showToast("error", "Please test the connection first");
      return;
    }
    setIsConnecting(true);
    connectFub({ variables: { companyId, apiKey } });
  };

  const handleDisconnect = () => {
    confirm({
      title: "Disconnect from Follow Up Boss?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This will remove all synced buyers from Fyrstup. This action cannot be undone.",
      okText: "Disconnect",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        disconnectFub({ variables: { companyId } });
      },
    });
  };

  const handleTriggerSync = () => {
    confirm({
      title: "Trigger Manual Sync?",
      icon: <SyncOutlined />,
      content:
        "This will sync all buyers from Follow Up Boss. This may take several minutes.",
      okText: "Start Sync",
      cancelText: "Cancel",
      okButtonProps: {
        style: { backgroundColor: "#1890ff", borderColor: "#1890ff" },
      },
      onOk() {
        triggerFubSync({ variables: { companyId } });
      },
    });
  };

  if (loading) return <Spinner />;

  return (
    <div className={embedded ? "" : "m-4"}>
      {!embedded && (
        <Breadcrumb
          title="Follow Up Boss Settings"
          items={[
            { title: "Dashboard", href: `/company/${companyId}/dashboard` },
            { title: "FUB Settings" },
          ]}
        />
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${embedded ? "mt-2" : "mt-6"}`}>
        {/* Connection Status Card */}
        <Card
          title={
            <span className="flex items-center gap-2">
              {settings?.isConnected ? (
                <CheckCircleOutlined className="text-green-500" />
              ) : (
                <CloseCircleOutlined className="text-gray-400" />
              )}
              Connection Status
            </span>
          }
        >
          {settings?.isConnected ? (
            <div className="space-y-4">
              <Alert
                message="Connected to Follow Up Boss"
                description={`Last sync: ${
                  settings.lastSyncAt
                    ? new Date(settings.lastSyncAt).toLocaleString()
                    : "Never"
                }`}
                type="success"
                showIcon
              />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total Buyers Imported:</span>
                  <span className="ml-2 font-semibold">
                    {settings.totalBuyersImported || 0}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Last Sync Status:</span>
                  <Tag
                    color={
                      settings.lastSyncStatus === "success"
                        ? "green"
                        : settings.lastSyncStatus === "partial"
                        ? "orange"
                        : "red"
                    }
                    className="ml-2"
                  >
                    {settings.lastSyncStatus || "N/A"}
                  </Tag>
                </div>
              </div>

              {settings.lastSyncError && (
                <Alert
                  message="Last Sync Error"
                  description={settings.lastSyncError}
                  type="warning"
                  showIcon
                />
              )}

              {settings.isSyncing && (
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    <SyncOutlined spin className="mr-2" />
                    Syncing buyers from Follow Up Boss...
                  </span>
                  <Progress percent={50} status="active" showInfo={false} />
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button
                  icon={<SyncOutlined spin={settings.isSyncing} />}
                  onClick={handleTriggerSync}
                  loading={triggering}
                  disabled={settings.isSyncing}
                >
                  {settings.isSyncing ? "Syncing..." : "Manual Sync"}
                </Button>
                <Button
                  danger
                  icon={<DisconnectOutlined />}
                  onClick={handleDisconnect}
                  loading={disconnecting}
                >
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert
                message="Not Connected"
                description="Enter your Follow Up Boss API key to connect and start syncing buyers."
                type="info"
                showIcon
              />

              <Input.Password
                placeholder="Enter FUB API Key (fka_...)"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                size="large"
              />

              {testResult && (
                <Alert
                  message={testResult.success ? "Connection Successful" : "Connection Failed"}
                  description={testResult.message}
                  type={testResult.success ? "success" : "error"}
                  showIcon
                />
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleTestConnection}
                  loading={testingConnection}
                  disabled={!apiKey.trim()}
                >
                  Test Connection
                </Button>
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={handleConnect}
                  loading={connecting || isConnecting}
                  disabled={!testResult?.success}
                  style={{ backgroundColor: "#b4c540", borderColor: "#b4c540" }}
                >
                  Connect
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Stage Configuration Card (Read-only) */}
        {settings?.isConnected && (
          <Card title="Stage Configuration">
            <p className="text-gray-600 text-sm mb-4">
              How Follow Up Boss stages are mapped to Fyrstup:
            </p>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-green-600 mb-2">
                  Active Stages (Import to Fyrstup)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_ACTIVE_STAGES.map((stage) => (
                    <Tag key={`active-${stage}`} color="green">
                      {stage}
                    </Tag>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-red-600 mb-2">
                  Closed Stages (Remove from Fyrstup)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_CLOSED_STAGES.map((stage) => (
                    <Tag key={`closed-${stage}`} color="red">
                      {stage}
                    </Tag>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-600 mb-2">
                  Ignored Stages (Skip)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_IGNORE_STAGES.map((stage) => (
                    <Tag key={`ignore-${stage}`} className="opacity-50">
                      {stage}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Info Card */}
      <Card className="mt-6" title="About Follow Up Boss Integration">
        <div className="text-gray-600 space-y-2">
          <p>
            <strong>How it works:</strong> Once connected, Fyrstup will automatically
            sync buyers from Follow Up Boss based on your stage configuration.
          </p>
          <p>
            <strong>Buyer Pool:</strong> Buyers without an assigned agent in FUB will
            appear in the Buyer Pool, where any agent can claim them.
          </p>
          <p>
            <strong>Automatic Updates:</strong> Changes made in Follow Up Boss are
            automatically synced to Fyrstup via webhooks.
          </p>
          <p>
            <strong>Monthly Reconciliation:</strong> A monthly sync ensures no buyers
            are missed due to webhook failures.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default FUBSettings;
